/**
 * The window and requests of Calendar's first screen.
 *
 * Calendar fetches a fixed window around today: from 30 days before to 91
 * days after the current UTC midnight. Because the window depends only on the
 * date, the shell can send the screen's requests when Calendar is clicked,
 * while its code still loads (platform/performance/applicationOpen.ts). The
 * view and the shell build the URLs here, so the shell's requests match the
 * view's byte for byte.
 */

const DAY_MS = 24 * 60 * 60 * 1000;
const WINDOW_BACK_MS = 30 * DAY_MS;
const WINDOW_FORWARD_MS = 90 * DAY_MS;

/** Rows per `/records` page the calendar requests. */
export const CALENDAR_TASK_PAGE_SIZE = 500;

export interface CalendarWindow {
  min: Date;
  max: Date;
}

/** The window Calendar fetches and syncs when it mounts at `now`. */
export function calendarMountWindow(now: Date): CalendarWindow {
  const midnight = new Date(now.getTime());
  midnight.setUTCHours(0, 0, 0, 0);
  return {
    min: new Date(midnight.getTime() - WINDOW_BACK_MS),
    max: new Date(midnight.getTime() + WINDOW_FORWARD_MS + DAY_MS),
  };
}

/** `/calendar-events` for one range of ISO instants. */
export function calendarEventRangePath(timeMin: string, timeMax: string): string {
  return `/calendar-events?${new URLSearchParams({ time_min: timeMin, time_max: timeMax })}`;
}

/**
 * The `/records` query for one page of the tasks that start, or fall due,
 * inside a window. Assignee filters are added by the caller.
 */
export function calendarTaskPageParams(
  page: number,
  dateField: 'start' | 'due',
  timeMin: string,
  timeMax: string
) {
  return {
    page,
    page_size: CALENDAR_TASK_PAGE_SIZE,
    ...(dateField === 'start'
      ? { start_date_from: timeMin, start_date_to: timeMax }
      : { due_date_from: timeMin, due_date_to: timeMax }),
    // Calendar only consumes assignees; skipping the other populates cuts
    // about three backend round trips per page.
    include: 'assignees',
  };
}

function taskPagePath(dateField: 'start' | 'due', timeMin: string, timeMax: string): string {
  const params = calendarTaskPageParams(1, dateField, timeMin, timeMax);
  const query = new URLSearchParams(
    Object.entries(params).map(([name, value]) => [name, String(value)])
  );
  return `/records?${query}`;
}

/**
 * The requests Calendar's events wait on when it opens at `now` with nothing
 * selected. The calendar list (configs, Calendars, connections) is left out:
 * Home's agenda has usually loaded it, and the view then sends none of it,
 * except the configs the event query reads itself.
 */
export function calendarFirstScreenPaths(now: Date) {
  const window = calendarMountWindow(now);
  const timeMin = window.min.toISOString();
  const timeMax = window.max.toISOString();
  return {
    events: calendarEventRangePath(timeMin, timeMax),
    configs: '/calendar-sync/configs',
    recordsByStart: taskPagePath('start', timeMin, timeMax),
    recordsByDue: taskPagePath('due', timeMin, timeMax),
    shares: '/calendar-shares',
  };
}

export function calendarFirstScreenRequests(now: Date): string[] {
  return Object.values(calendarFirstScreenPaths(now));
}
